import { allowsAnyPath } from './endpoint-access';

/**
 * The shape this filter needs from a nav entry. Structural on purpose: the real `NavItem` lives
 * in `@realtyfeed/app-shell`, and importing it here would tie the permission model to the
 * shell's presentation types for no gain.
 */
export type PermissionGatedNavItem = {
  /**
   * Endpoint paths the entry's screen calls. The entry is shown when the session may use **at
   * least one** of them.
   *
   * A nav entry with no `apiPath` is visible to everyone. That is the right default for a
   * genuinely unrestricted entry (Log out) and a security regression for any other, which is
   * why `findUndeclaredNavEntries` exists and why there is a test calling it.
   */
  apiPath?: readonly string[];
  /** Permission names, as an alternative to `apiPath` for an entry with no natural endpoint. */
  permissions?: readonly string[];
};

/**
 * A nav tree whose groups nest entries of the caller's own type.
 *
 * `children?: T[]` rather than `children?: PermissionGatedNavItem[]` so the filter returns the
 * caller's type back, extra fields and all, instead of widening it to this structural minimum.
 */
export type PermissionGatedNavTree<T> = PermissionGatedNavItem & { children?: T[] };

/**
 * Drops the nav entries a session may not see.
 *
 * A group survives when at least one of its children does; a group whose children are all
 * denied is dropped rather than left as an empty expander. A group may also declare its own
 * `apiPath`, which must pass in addition.
 *
 * Returns new objects for groups whose children changed, so the caller can render the result
 * directly; entries that are kept whole are returned by reference.
 */
export const filterNavByPermission = <T extends PermissionGatedNavTree<T>>(
  items: readonly T[],
  granted: ReadonlySet<string>
): T[] => {
  const result: T[] = [];

  for (const item of items) {
    const ownPaths = item.apiPath ?? [];
    const ownNames = item.permissions ?? [];

    const passesOwnPaths = allowsAnyPath(granted, ownPaths);
    const passesOwnNames = ownNames.length === 0 || ownNames.some((name) => granted.has(name));

    if (!passesOwnPaths || !passesOwnNames) continue;

    if (item.children) {
      const children = filterNavByPermission(item.children, granted);
      if (children.length === 0) continue;
      result.push({ ...item, children });
      continue;
    }

    result.push(item);
  }

  return result;
};

/**
 * Every nav entry that declares no permission requirement at all, by id where there is one.
 *
 * **This is the check that matters most in the whole permission model.** Porting a screen means
 * copying its `apiPath` list across from the legacy nav config, and the failure mode of
 * forgetting is silent: the entry renders for everyone, looks completely normal, and only the
 * backend's 403 reveals it — to the user, not to us.
 *
 * `allowUnrestricted` lists the ids that legitimately have no requirement, so that the set this
 * returns can be asserted empty in a test. See `nav-access.test.ts`.
 */
export const findUndeclaredNavEntries = <T extends PermissionGatedNavTree<T> & { id?: string }>(
  items: readonly T[],
  allowUnrestricted: readonly string[] = []
): string[] => {
  const undeclared: string[] = [];

  const walk = (entries: readonly T[]) => {
    for (const entry of entries) {
      const id = entry.id ?? '<unnamed>';
      const declaresOwn = (entry.apiPath?.length ?? 0) > 0 || (entry.permissions?.length ?? 0) > 0;

      if (entry.children) {
        // A group is allowed to carry nothing itself as long as its children are gated — the
        // group disappears when they all do.
        walk(entry.children);
        continue;
      }

      if (!declaresOwn && !allowUnrestricted.includes(id)) undeclared.push(id);
    }
  };

  walk(items);
  return undeclared;
};
